import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ShoppingCart, AlertTriangle, TrendingUp, TrendingDown, Clock, 
  Package, Search, RefreshCw, Download, Zap, Filter, ArrowUpDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCHES = {
  "all": "All Branches",
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

export default function PurchaseRequest() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("priority"); // priority, name, dtl, ado
  const [days, setDays] = useState(30);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const [daysToLastRes, matrixRes] = await Promise.all([
        axios.get(`${API}/analytics/days-to-last`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { branch: selectedBranch !== "all" ? selectedBranch : undefined, days }
        }),
        axios.get(`${API}/global-inventory-matrix`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { show_zero: true }
        })
      ]);

      // Process and merge data
      // Backend returns critical, low, out_of_stock, ok - combine them all
      const daysToLastData = daysToLastRes.data || {};
      const daysToLastItems = [
        ...(daysToLastData.critical || []),
        ...(daysToLastData.low || []),
        ...(daysToLastData.out_of_stock || []),
        ...(daysToLastData.ok || [])
      ];
      const matrixItems = matrixRes.data?.items || [];
      
      // Create a map from matrix data for branch-level stock
      const matrixMap = {};
      matrixItems.forEach(item => {
        matrixMap[item.item_name] = item;
      });

      // Process items with purchase request calculations
      const processedItems = daysToLastItems.map(item => {
        const matrixItem = matrixMap[item.item_name] || {};
        const stock = item.current_stock || 0;
        const avgDaily = item.avg_daily || 0;
        const daysLeft = item.days_left;
        
        // Calculate suggested order quantity (target: 30 days of stock)
        const targetDays = 30;
        const targetStock = avgDaily * targetDays;
        const suggestedQty = Math.max(0, Math.ceil(targetStock - stock));
        
        // Calculate priority score
        let priorityScore = 0;
        let priorityLabel = "LOW";
        let priorityColor = "slate";
        
        if (stock === 0) {
          priorityScore = 100;
          priorityLabel = "CRITICAL";
          priorityColor = "red";
        } else if (typeof daysLeft === 'number') {
          if (daysLeft <= 30) {
            priorityScore = 90;
            priorityLabel = "HIGH";
            priorityColor = "red";
          } else if (daysLeft <= 60) {
            priorityScore = 70;
            priorityLabel = "MEDIUM";
            priorityColor = "amber";
          } else if (daysLeft <= 90) {
            priorityScore = 50;
            priorityLabel = "LOW";
            priorityColor = "yellow";
          } else {
            priorityScore = 20;
            priorityLabel = "OK";
            priorityColor = "green";
          }
        } else {
          priorityScore = 10;
          priorityLabel = "OK";
          priorityColor = "green";
        }
        
        // Adjust priority based on velocity (fast moving items get higher priority)
        if (avgDaily >= 5) {
          priorityScore += 15;
          // Bump up priority for fast-moving items
          if (priorityLabel === "OK") priorityLabel = "LOW";
          else if (priorityLabel === "LOW") priorityLabel = "MEDIUM";
        } else if (avgDaily >= 2) {
          priorityScore += 5;
        }
        
        // Calculate projected out date
        let projectedOutDate = null;
        if (typeof daysLeft === 'number' && daysLeft > 0) {
          const outDate = new Date();
          outDate.setDate(outDate.getDate() + Math.floor(daysLeft));
          projectedOutDate = outDate.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
        }
        
        // Velocity label
        let velocity = "NONE";
        if (avgDaily >= 5) velocity = "FAST";
        else if (avgDaily >= 2) velocity = "GOOD";
        else if (avgDaily >= 0.5) velocity = "SLOW";
        else if (avgDaily > 0) velocity = "VERY SLOW";
        
        return {
          ...item,
          branches: matrixItem.branches || {},
          total_stock: matrixItem.total_stock || stock,
          suggestedQty,
          priorityScore,
          priorityLabel,
          priorityColor,
          projectedOutDate,
          velocity
        };
      });

      // Filter to only show items that need restocking
      // Criteria: DTL <= 90 days, OR critical low stock, OR has suggested order qty
      const needsRestock = processedItems.filter(item => 
        item.priorityLabel === "CRITICAL" ||
        item.priorityLabel === "HIGH" ||
        item.priorityLabel === "MEDIUM" ||
        item.priorityLabel === "WATCH" ||
        item.current_stock === 0 ||
        item.suggestedQty > 0
      );

      setItems(needsRestock);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load purchase request data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch, days]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let result = items.filter(item => {
      const matchesSearch = !searchTerm || 
        item.item_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPriority = priorityFilter === "all" || item.priorityLabel === priorityFilter;
      return matchesSearch && matchesPriority;
    });

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "priority":
          return b.priorityScore - a.priorityScore;
        case "name":
          return a.item_name.localeCompare(b.item_name);
        case "dtl":
          const dtlA = typeof a.days_left === 'number' ? a.days_left : 9999;
          const dtlB = typeof b.days_left === 'number' ? b.days_left : 9999;
          return dtlA - dtlB;
        case "ado":
          return (b.avg_daily || 0) - (a.avg_daily || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [items, searchTerm, priorityFilter, sortBy]);

  // Get priority badge style
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case "CRITICAL": return "bg-red-600 text-white";
      case "HIGH": return "bg-red-500 text-white";
      case "MEDIUM": return "bg-amber-500 text-white";
      case "WATCH": return "bg-yellow-500 text-black";
      case "LOW": return "bg-green-500 text-white";
      case "OK": return "bg-slate-400 text-white";
      default: return "bg-slate-300 text-slate-700";
    }
  };

  // Export to CSV
  const exportToCsv = () => {
    const headers = ["Item Name", "Current Stock", "ADO", "DTL", "Priority", "Suggested Qty", "Projected Out"];
    const rows = filteredItems.map(item => [
      item.item_name,
      item.current_stock,
      item.avg_daily,
      item.days_left,
      item.priorityLabel,
      item.suggestedQty,
      item.projectedOutDate || "-"
    ]);

    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Purchase_Request_${selectedBranch}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported to CSV!");
  };

  // Summary counts
  const summary = useMemo(() => ({
    critical: items.filter(i => i.priorityLabel === "CRITICAL").length,
    high: items.filter(i => i.priorityLabel === "HIGH").length,
    medium: items.filter(i => i.priorityLabel === "MEDIUM").length,
    watch: items.filter(i => i.priorityLabel === "WATCH").length,
    total: items.length
  }), [items]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="purchase-request">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-indigo-600" />
              Purchase Request
            </h1>
            <p className="text-slate-500">Items that need restocking based on sales velocity</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-[100px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-[150px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BRANCHES).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            <Button variant="outline" onClick={exportToCsv} disabled={filteredItems.length === 0}>
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className="bg-red-600 text-white cursor-pointer" onClick={() => setPriorityFilter(priorityFilter === "CRITICAL" ? "all" : "CRITICAL")}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-xs">CRITICAL</p>
                <p className="text-2xl font-bold">{summary.critical}</p>
              </div>
              <AlertTriangle className="h-6 w-6 text-red-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-500 text-white cursor-pointer" onClick={() => setPriorityFilter(priorityFilter === "HIGH" ? "all" : "HIGH")}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-xs">HIGH</p>
                <p className="text-2xl font-bold">{summary.high}</p>
              </div>
              <TrendingDown className="h-6 w-6 text-red-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-500 text-white cursor-pointer" onClick={() => setPriorityFilter(priorityFilter === "MEDIUM" ? "all" : "MEDIUM")}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs">MEDIUM</p>
                <p className="text-2xl font-bold">{summary.medium}</p>
              </div>
              <Clock className="h-6 w-6 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-500 text-black cursor-pointer" onClick={() => setPriorityFilter(priorityFilter === "WATCH" ? "all" : "WATCH")}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-800 text-xs">WATCH</p>
                <p className="text-2xl font-bold">{summary.watch}</p>
              </div>
              <Zap className="h-6 w-6 text-yellow-700" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-700 text-white cursor-pointer" onClick={() => setPriorityFilter("all")}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-xs">TOTAL</p>
                <p className="text-2xl font-bold">{summary.total}</p>
              </div>
              <Package className="h-6 w-6 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px]">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="priority">Sort: Priority</SelectItem>
                <SelectItem value="name">Sort: Name</SelectItem>
                <SelectItem value="dtl">Sort: Days Left</SelectItem>
                <SelectItem value="ado">Sort: ADO</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Package className="h-12 w-12 mb-2 opacity-50" />
              <p>No items need restocking</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Item Name</th>
                    <th className="text-center px-3 py-3 font-semibold text-slate-700">Priority</th>
                    <th className="text-right px-3 py-3 font-semibold text-slate-700">Stock</th>
                    <th className="text-right px-3 py-3 font-semibold text-blue-600">ADO</th>
                    <th className="text-right px-3 py-3 font-semibold text-amber-600">DTL</th>
                    <th className="text-center px-3 py-3 font-semibold text-slate-700">Velocity</th>
                    <th className="text-center px-3 py-3 font-semibold text-slate-700">Proj. Out</th>
                    <th className="text-right px-3 py-3 font-semibold text-indigo-600">Suggested Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredItems.map((item, idx) => (
                    <tr key={idx} className={`hover:bg-slate-50 ${
                      item.priorityLabel === "CRITICAL" ? "bg-red-50" :
                      item.priorityLabel === "HIGH" ? "bg-red-50/50" :
                      item.priorityLabel === "MEDIUM" ? "bg-amber-50/50" :
                      ""
                    }`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{item.item_name}</td>
                      <td className="px-3 py-3 text-center">
                        <Badge className={`${getPriorityStyle(item.priorityLabel)} text-xs`}>
                          {item.priorityLabel}
                        </Badge>
                      </td>
                      <td className={`px-3 py-3 text-right font-semibold ${item.current_stock === 0 ? 'text-red-600' : ''}`}>
                        {item.current_stock}
                      </td>
                      <td className="px-3 py-3 text-right text-blue-600 font-medium">{item.avg_daily}</td>
                      <td className={`px-3 py-3 text-right font-semibold ${
                        item.days_left === 0 ? 'text-red-600' :
                        typeof item.days_left === 'number' && item.days_left <= 7 ? 'text-red-600' :
                        typeof item.days_left === 'number' && item.days_left <= 14 ? 'text-amber-600' :
                        'text-slate-600'
                      }`}>
                        {item.days_left === "∞" ? "∞" : item.days_left}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          item.velocity === "FAST" ? "bg-green-100 text-green-700" :
                          item.velocity === "GOOD" ? "bg-blue-100 text-blue-700" :
                          item.velocity === "SLOW" ? "bg-yellow-100 text-yellow-700" :
                          item.velocity === "VERY SLOW" ? "bg-orange-100 text-orange-700" :
                          "bg-slate-100 text-slate-500"
                        }`}>
                          {item.velocity}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center text-slate-600">
                        {item.projectedOutDate || "-"}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                          +{item.suggestedQty}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
